import { useRef, useState, type ReactNode } from 'react';
import { clsx } from '../../ds/clsx';
import {
  Banner,
  Button,
  Card,
  Eyebrow,
  Pill,
  Select,
  Sheet,
  Skeleton,
  TextField,
  TextLink,
  Toggle,
} from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { errorMessage } from '../../lib/api';
import { isIos } from '../../lib/platform';
import { usePush, type PushControl } from '../../lib/push';
import { SettingsPageHeader, useSavedPill } from './SettingsFrame';
import {
  channelSummary,
  channels,
  groupEvents,
  groupLabels,
  useNotificationSettings,
  useUpdateNotificationSettings,
  type Channel,
  type NotificationSettings,
  type SettingsEvent,
  type SettingsUpdate,
} from './notificationSettings';
import styles from './NotificationsSettingsPage.module.css';

const PUSH_LOCKED_STATES = new Set(['unavailable', 'unsupported', 'denied']);

function pushLocked(push: PushControl): boolean {
  return PUSH_LOCKED_STATES.has(push.state);
}

/** Push status of this device (D-05): the action to subscribe, or why it is not possible. */
function PushStatus({ push }: { push: PushControl }) {
  switch (push.state) {
    case 'checking':
      return <Skeleton width={120} />;
    case 'ready':
      return (
        <Button variant="secondary" size="small" loading={push.pending} onClick={() => void push.enable()}>
          Push aktivieren
        </Button>
      );
    case 'subscribed':
      return (
        <span className={styles.pushRow}>
          <Pill variant="success" dot>
            Push auf diesem Gerät aktiv
          </Pill>
          <TextLink
            as="button"
            size="small"
            tone="muted"
            disabled={push.pending}
            onClick={() => void push.disable()}
          >
            Push deaktivieren
          </TextLink>
        </span>
      );
    case 'denied':
      return (
        <Pill variant="warning" dot>
          Push abgelehnt
        </Pill>
      );
    case 'unsupported':
    case 'unavailable':
      return <Pill variant="neutral">Push nicht verfügbar</Pill>;
  }
}

/** EM7: the browser refused notifications; the system settings are the only way back (no deep link from the web). */
function PushDeniedCard() {
  const ios = isIos();
  return (
    <div className={styles.deniedCard}>
      <div className={styles.deniedTitle}>Push ist deaktiviert</div>
      <div className={styles.deniedText}>
        {ios
          ? 'Sie haben Mitteilungen für DAMOKLES im Systemdialog abgelehnt. Push lässt sich nur in den iOS-Einstellungen wieder erlauben.'
          : 'Sie haben Mitteilungen für DAMOKLES im Systemdialog abgelehnt. Push lässt sich nur in den Browser- oder Systemeinstellungen wieder erlauben.'}
      </div>
    </div>
  );
}

interface ChannelCellProps {
  event: SettingsEvent;
  channel: Channel;
  locked: boolean;
  onChange: (update: SettingsUpdate) => void;
}

function ChannelToggle({ event, channel, locked, onChange }: ChannelCellProps) {
  const label = channels.find((entry) => entry.key === channel)?.label ?? channel;
  return (
    <span className={styles.cell}>
      <Toggle
        label={`${label} für ${event.name}`}
        labelHidden
        checked={event[channel]}
        locked={locked}
        onChange={(checked) => onChange({ events: [{ key: event.key, [channel]: checked }] })}
      />
    </span>
  );
}

function LeadTimeSelect({
  event,
  onChange,
}: {
  event: SettingsEvent;
  onChange: (update: SettingsUpdate) => void;
}) {
  if (!event.leadTimePossible) return <span />;
  return (
    <Select
      label={`Vorlauf für ${event.name}`}
      labelHidden
      size="compact"
      value={event.leadTimeText ?? ''}
      options={event.leadTimeOptions.map((option) => ({ value: option, label: option }))}
      onChange={(changeEvent) =>
        onChange({ events: [{ key: event.key, leadTimeText: changeEvent.target.value }] })
      }
    />
  );
}

/** Shell 15 matrix: event × lead time × four channels, grouped. */
function DesktopMatrix({
  settings,
  push,
  onChange,
}: {
  settings: NotificationSettings;
  push: PushControl;
  onChange: (update: SettingsUpdate) => void;
}) {
  const pushOff = pushLocked(push);
  return (
    <Card className={styles.matrixCard}>
      <div className={clsx(styles.row, styles.headRow)} role="presentation">
        <Eyebrow>Ereignis</Eyebrow>
        <Eyebrow>Vorlauf</Eyebrow>
        <Eyebrow className={styles.center}>In der App</Eyebrow>
        <Eyebrow className={styles.center}>E-Mail</Eyebrow>
        <Eyebrow className={clsx(styles.center, styles.headStack)}>
          Push
          {pushOff ? <span className={styles.headNote}>Nicht verfügbar</span> : null}
        </Eyebrow>
        <Eyebrow className={clsx(styles.center, styles.headStack)}>
          SMS
          {settings.events.some((event) => !event.smsAvailable) ? (
            <span className={styles.headNote}>Nicht verfügbar</span>
          ) : null}
        </Eyebrow>
      </div>
      {groupEvents(settings.events).map((entry) => (
        <div key={entry.group} className={styles.group}>
          <Eyebrow as="div" className={styles.groupLabel}>
            {groupLabels[entry.group]}
          </Eyebrow>
          {entry.events.map((event) => (
            <div key={event.key} className={styles.row}>
              <span>
                <span className={styles.eventName}>{event.name}</span>
                {event.subtext ? <span className={styles.eventSub}>{event.subtext}</span> : null}
              </span>
              <LeadTimeSelect event={event} onChange={onChange} />
              {channels.map((channel) => (
                <ChannelToggle
                  key={channel.key}
                  event={event}
                  channel={channel.key}
                  locked={
                    event.mandatory ||
                    (channel.key === 'push' && pushOff) ||
                    (channel.key === 'sms' && !event.smsAvailable)
                  }
                  onChange={onChange}
                />
              ))}
            </div>
          ))}
        </div>
      ))}
    </Card>
  );
}

/** Ruhezeiten + Zusammenfassung (Shell 15 card, editable as in Einstellungen 15). */
function RulesCard({
  settings,
  onChange,
  layout,
}: {
  settings: NotificationSettings;
  onChange: (update: SettingsUpdate) => void;
  layout: 'desktop' | 'sheet';
}) {
  const quiet = settings.quietHours;
  const digest = settings.digest;
  const quietOn = quiet.start !== null && quiet.end !== null;
  return (
    <div className={clsx(styles.rules, layout === 'sheet' && styles.rulesStacked)}>
      <div>
        <Eyebrow as="div">Ruhezeiten</Eyebrow>
        <div className={styles.ruleControls}>
          <Toggle
            label="Ruhezeiten aktiv"
            labelHidden
            checked={quietOn}
            onChange={(checked) =>
              onChange({
                quietHours: checked
                  ? { start: quiet.start ?? '20:00', end: quiet.end ?? '07:30' }
                  : { start: null, end: null },
              })
            }
          />
          <TextField
            label="Beginn"
            labelHidden
            type="time"
            size="compact"
            className={styles.timeField}
            value={quiet.start ?? '20:00'}
            disabled={!quietOn}
            onChange={(event) => onChange({ quietHours: { start: event.target.value || quiet.start } })}
          />
          <span className={styles.ruleWord}>bis</span>
          <TextField
            label="Ende"
            labelHidden
            type="time"
            size="compact"
            className={styles.timeField}
            value={quiet.end ?? '07:30'}
            disabled={!quietOn}
            onChange={(event) => onChange({ quietHours: { end: event.target.value || quiet.end } })}
          />
          <Toggle
            label="und am Wochenende"
            checked={quiet.weekend}
            onChange={(checked) => onChange({ quietHours: { weekend: checked } })}
          />
        </div>
        <div className={styles.ruleText}>
          Push und SMS werden gesammelt. Ausnahme: Umbuchung oder Absage am selben Tag.
        </div>
      </div>
      <div>
        <Eyebrow as="div">Zusammenfassung</Eyebrow>
        <div className={styles.ruleControls}>
          <Toggle
            label="Tägliche Zusammenfassung"
            labelHidden
            checked={digest.enabled}
            onChange={(checked) => onChange({ digest: { enabled: checked } })}
          />
          <span className={styles.ruleWord}>Täglich</span>
          <TextField
            label="Uhrzeit der Zusammenfassung"
            labelHidden
            type="time"
            size="compact"
            className={styles.timeField}
            value={digest.time}
            disabled={!digest.enabled}
            onChange={(event) => onChange({ digest: { time: event.target.value || digest.time } })}
          />
          <span className={styles.ruleWord}>per E-Mail</span>
        </div>
        <div className={styles.ruleText}>Alles, was über Nacht passiert ist, und die Tasks des Tages.</div>
      </div>
    </div>
  );
}

/** EM7: one row per event with a summary and an on/off switch; the row opens the channel sheet. */
function MobileList({
  settings,
  push,
  onChange,
}: {
  settings: NotificationSettings;
  push: PushControl;
  onChange: (update: SettingsUpdate) => void;
}) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const remembered = useRef(new Map<string, Record<Channel, boolean>>());
  const pushOff = pushLocked(push);
  const open = settings.events.find((event) => event.key === openKey) ?? null;
  const quiet = settings.quietHours;

  function toggleEvent(event: SettingsEvent, on: boolean) {
    if (!on) {
      remembered.current.set(event.key, {
        app: event.app,
        email: event.email,
        push: event.push,
        sms: event.sms,
      });
      onChange({ events: [{ key: event.key, app: false, email: false, push: false, sms: false }] });
      return;
    }
    const previous = remembered.current.get(event.key) ?? {
      app: true,
      email: false,
      push: false,
      sms: false,
    };
    onChange({ events: [{ key: event.key, ...previous }] });
  }

  return (
    <div className={styles.mobile}>
      {groupEvents(settings.events).map((entry) => (
        <div key={entry.group} className={styles.mobileGroup}>
          <Eyebrow as="div" className={styles.mobileGroupLabel}>
            {groupLabels[entry.group]}
          </Eyebrow>
          <div className={styles.mobileCard}>
            {entry.events.map((event) => {
              const anyOn = event.app || event.email || event.push || event.sms;
              return (
                <div key={event.key} className={styles.mobileRow}>
                  <button
                    type="button"
                    className={styles.mobileRowText}
                    onClick={() => setOpenKey(event.key)}
                  >
                    <span className={styles.mobileRowName}>{event.name}</span>
                    <span className={styles.mobileRowSub}>{event.subtext ?? channelSummary(event)}</span>
                  </button>
                  <Toggle
                    label={event.name}
                    labelHidden
                    size="large"
                    checked={anyOn}
                    locked={event.mandatory}
                    onChange={(checked) => toggleEvent(event, checked)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {push.state === 'denied' ? <PushDeniedCard /> : null}
      {push.state === 'ready' || push.state === 'subscribed' ? (
        <div className={styles.pushCard}>
          <span className={styles.pushCardText}>
            <span className={styles.mobileRowName}>Push auf diesem Gerät</span>
            <span className={styles.mobileRowSub}>
              {push.state === 'subscribed' ? 'Aktiv' : 'Noch nicht aktiviert'}
            </span>
          </span>
          <PushStatus push={push} />
        </div>
      ) : null}
      {push.error ? <Banner tone="danger">{push.error}</Banner> : null}

      <button type="button" className={styles.pushCard} onClick={() => setRulesOpen(true)}>
        <span className={styles.pushCardText}>
          <span className={styles.mobileRowName}>Ruhezeiten</span>
          <span className={clsx(styles.mobileRowSub, styles.tabular)}>
            {quiet.start && quiet.end ? `${quiet.start} – ${quiet.end}` : 'Aus'}
            {settings.digest.enabled ? ` · Zusammenfassung ${settings.digest.time}` : ''}
          </span>
        </span>
        <span className={styles.chevron} aria-hidden="true">
          ›
        </span>
      </button>

      <Sheet
        open={open !== null}
        onClose={() => setOpenKey(null)}
        title={open?.name}
        subtitle={open?.subtext ?? undefined}
      >
        {open ? (
          <div className={styles.sheetBody}>
            {open.leadTimePossible ? (
              <Select
                label="Vorlauf"
                value={open.leadTimeText ?? ''}
                options={open.leadTimeOptions.map((option) => ({ value: option, label: option }))}
                onChange={(event) =>
                  onChange({ events: [{ key: open.key, leadTimeText: event.target.value }] })
                }
              />
            ) : null}
            {channels.map((channel) => (
              <div key={channel.key} className={styles.sheetRow}>
                <Toggle
                  label={channel.label}
                  size="large"
                  checked={open[channel.key]}
                  locked={
                    open.mandatory ||
                    (channel.key === 'push' && pushOff) ||
                    (channel.key === 'sms' && !open.smsAvailable)
                  }
                  onChange={(checked) => onChange({ events: [{ key: open.key, [channel.key]: checked }] })}
                />
                {channel.key === 'push' && pushOff ? (
                  <span className={styles.sheetNote}>Nicht verfügbar</span>
                ) : null}
                {channel.key === 'sms' && !open.smsAvailable ? (
                  <span className={styles.sheetNote}>Nicht verfügbar</span>
                ) : null}
              </div>
            ))}
            {open.mandatory ? <div className={styles.sheetNote}>Vorgegeben von der Organisation.</div> : null}
          </div>
        ) : null}
      </Sheet>

      <Sheet open={rulesOpen} onClose={() => setRulesOpen(false)} title="Ruhezeiten und Zusammenfassung">
        <RulesCard settings={settings} onChange={onChange} layout="sheet" />
      </Sheet>
    </div>
  );
}

function PageSkeleton({ children }: { children?: ReactNode }) {
  return (
    <div className={styles.skeleton} aria-busy="true">
      <Skeleton variant="block" height={420} />
      <Skeleton variant="block" height={120} />
      {children}
    </div>
  );
}

/** `/settings/notifications` — Shell 15 in the frame of Einstellungen 15, EM7 on mobile. */
export function NotificationsSettingsPage() {
  const mobile = useIsMobile();
  const { markSaved } = useSavedPill();
  const settings = useNotificationSettings();
  const update = useUpdateNotificationSettings(markSaved);
  const push = usePush();

  function change(next: SettingsUpdate) {
    update.mutate(next);
  }

  return (
    <div className={styles.page}>
      <SettingsPageHeader
        eyebrow="Meine Einstellungen"
        title="Benachrichtigungen"
        description="Wählen Sie je Ereignis, wie und wie früh Sie erfahren sollen."
        actions={mobile ? undefined : <PushStatus push={push} />}
      />
      {push.error && !mobile ? <Banner tone="danger">{push.error}</Banner> : null}
      {update.isError ? <Banner tone="danger">{errorMessage(update.error)}</Banner> : null}
      {settings.isError ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void settings.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(settings.error)}
        </Banner>
      ) : settings.isPending ? (
        <PageSkeleton />
      ) : mobile ? (
        <MobileList settings={settings.data} push={push} onChange={change} />
      ) : (
        <>
          <DesktopMatrix settings={settings.data} push={push} onChange={change} />
          <Card className={styles.rulesCard}>
            <RulesCard settings={settings.data} onChange={change} layout="desktop" />
          </Card>
        </>
      )}
    </div>
  );
}
